import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Subscription, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { FileObject, mapMediaToFileObject } from './../../../../core/interfaces/file-object';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { PatientAdmissionHistoryService } from './../../../../core/services/patient-admission-history.service';
import { PatientAppointmentService } from './../../../../core/services/patient-appointment.service';
import { PatientRiskItemService } from './../../../../core/services/patient-risk-item.service';
import { PatientService } from './../../../../core/services/patient.service';
import {
  RiskLocationPreview,
  RiskPreviewHospital,
  RiskPreviewWard,
  previewRiskLocation,
} from './../../../../core/utils/risk-location-preview';
import {
  RISK_CATEGORIES,
  RISK_SEVERITIES,
  RISK_STATUSES,
  RiskFacilityChoice,
  pickEncounterAppointment,
  riskCategoryLabel,
  riskSeverityLabel,
} from './../../../../shared/components/risk-suggestion-list/risk-suggestion.model';

/**
 * WORKSTREAM N — the ROUTED risk-item detail page (§11).
 *
 * > "instead of a dialog when manually adding, we should follow patterns from patient-note-detail,
 * > own component, nav to it, show delete button if there is an id, etc. and allow these items to
 * > be edited from in the patient file (same as timeline notes)"
 *
 * Structurally a copy of `patient-task-detail.component.ts` — the same `mat-toolbar` with back /
 * delete / save, the same delete gated on `editing… && editing….id`, the same reactive form fed by
 * a `…$` hand-off channel the caller sets BEFORE navigating, the same `returnToParent`. It
 * retires deviation L-1, where the sidebar `+` delegated to the shared add DIALOG through a
 * `@ViewChild` because no page existed.
 *
 * ── WHAT IS NOT A COPY OF THE TASK TEMPLATE ───────────────────────────────────────────────
 *
 *  1. THERE IS NO `getRiskItemById`. `patient-task-detail` re-fetches its row from the route id;
 *     `routes/clinical/patientRoutes.js` exposes no GET-by-id for risk items, only the group and
 *     patient collections. So the row arrives on `editingRiskItem$` (set by the caller) and, for a
 *     reload or a deep link where that channel is empty, is recovered from `patientRiskItems$` —
 *     the patient-scoped list this page's own patient already has loaded.
 *
 *  2. `dateOfIssue` IS A `DATEONLY`. The task template sends `date.utc().startOf('day')
 *     .toISOString()`; doing that here would shift the calendar day at the facility (§5c) by one
 *     for any browser east of UTC. It is formatted `'YYYY-MM-DD'`, exactly as the add dialog's
 *     call site already did.
 *
 *  3. THE FACILITY PICKER IS EDITABLE ON BOTH ROUTES — see `applyStoredFacility`. It was locked on
 *     edit until 2026-09-01, because the server's update whitelist dropped the four columns; the
 *     user's decision (*"facility should be editable, we can leave it up to the user"*) unblocked
 *     it and `updateRiskItem` now takes the same `facilityChoice` envelope the POST does. There is
 *     no `reportedAt` gate on either side — explicitly asked for, and explicitly not to be added.
 *
 * ── THE DIALOG IS NOT DELETED, AND MUST NOT BE ────────────────────────────────────────────
 *
 * `risk-item-add-dialog` remains the AI-SUGGESTION accept path in both `patient-ai-request-detail`
 * and `patient-note-detail` (§11, confirmed by the user). A doctor triaging several suggestions
 * against one note stays on that note; routing away per suggestion is worse. This page replaces
 * the MANUAL add and is the EDIT surface. The two coexist deliberately.
 */
@Component({
  selector: 'app-patient-risk-detail',
  templateUrl: './patient-risk-detail.component.html',
  styleUrls: ['./patient-risk-detail.component.scss']
})
export class PatientRiskDetailComponent extends BaseComponent implements OnInit {
  todayDate: moment.Moment = moment();
  form!: FormGroup;
  editingRiskItem: any;
  patientId!: string;
  groupId!: string;
  patient: any;

  /**
   * D6/D7/D9 BY IMPORT — never restated. A closed taxonomy written out a second time is a closed
   * taxonomy waiting to disagree with itself, which is the whole reason `RISK_CATEGORIES`,
   * `RISK_SEVERITIES` and `RISK_STATUSES` are single definitions in `risk-suggestion.model.ts`.
   */
  readonly categoryOptions: readonly string[] = RISK_CATEGORIES;
  readonly severityOptions: readonly string[] = RISK_SEVERITIES;
  readonly statusOptions: readonly string[] = RISK_STATUSES;

  /** `null` on `add-risk-item`; the row id on `edit-risk-item/:id`. Read from the route only. */
  riskItemId: string | null = null;

  /** Active hospitals for the practice, WARDS NESTED, name-sorted. Possibly empty. */
  hospitals: RiskPreviewHospital[] = [];
  /** Wards of the currently selected hospital, name-sorted. */
  wards: RiskPreviewWard[] = [];

  /**
   * THE IN-ROOMS / OPD SENTINEL — the first option of the single facility select.
   *
   * The mode toggle and the hospital select used to be two controls expressing one choice, and two
   * controls can disagree: `facilityMode: 'facility'` with no hospital picked was a reachable,
   * unsaveable state that needed its own guard, its own warning banner and its own `canSave` term.
   * One select cannot hold that state — picking a hospital IS picking the mode — so all three are
   * gone. `facilityMode` survives as a DERIVED value because D6, `buildFacilityChoice` and the
   * stored `location` column all still speak in those terms.
   *
   * A literal rather than an id, so it can never collide with a hospital UUID. Declared per
   * component rather than shared: `risk-suggestion.model.ts` is the SERVER contract (the four
   * stored columns), and a sentinel that never leaves the browser does not belong in it.
   */
  readonly roomsValue = '__rooms__';

  /** The §4 preview that PRE-FILLS the picker on the add route. A default, never a prediction. */
  facilityPreview: RiskLocationPreview | null = null;

  /**
   * True once the doctor has moved one of the three facility controls.
   *
   * Two jobs, both borrowed from the dialog. It decides whether a `facilityChoice` is posted at
   * all (an untouched picker posts nothing, so the server's §4 chain still covers the one case the
   * browser cannot — a hospital DEACTIVATED since the admission was recorded resolves to an id
   * server-side and to a bare name here). And it stops a late-arriving `hospitals$` /
   * `patientAppointments$` emission from re-running the preview over a choice already made.
   */
  facilityTouched = false;

  private admissionRecords: any[] = [];
  private appointments?: any[];
  private saving = false;
  private routeParamsSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private patientService: PatientService,
    private hospitalService: HospitalService,
    private patientAdmissionHistoryService: PatientAdmissionHistoryService,
    private patientAppointmentService: PatientAppointmentService,
    private riskItemService: PatientRiskItemService,
    private cd: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();

    // FIRST, and synchronously: every subscription below keys off `riskItemId` to tell the add
    // route from the edit route.
    this.routeParamsSub = this.route.paramMap.subscribe(params => {
      this.riskItemId = params.get('id');
      if (!this.riskItemId) {
        this.resetForm();
      }
    });

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patient = selectedPatient;
            this.patientId = selectedPatient.id;
            this.form.patchValue({ patientId: this.patientId, groupId: patientGroupId(this.patient, this.groupId) });
            this.refreshFacilityPreview();
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    // ── The three feeds the §4 preview reads. All `providedIn: 'root'` and already loaded by the
    //    patient file this page was opened from, so they survive the outlet navigation.
    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hospitals => {
        this.hospitals = (Array.isArray(hospitals) ? hospitals : [])
          .filter((hospital: any) => !!hospital && !!hospital.id)
          .slice()
          .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')));
        // The list can arrive after the row does, so whichever of the two is the authority for
        // this route re-applies itself.
        if (this.isEditing) {
          this.applyStoredFacility();
        } else {
          this.refreshFacilityPreview();
        }
      });

    // Emits `[]` both for "no admissions" and for "the fetch failed"; an empty list is therefore
    // never treated as proof of an in-rooms issue (§4 branch 3).
    this.patientAdmissionHistoryService.patientAdmissionRecords$
      .pipe(takeUntil(this.destroy$))
      .subscribe(records => {
        this.admissionRecords = Array.isArray(records) ? records : [];
        this.refreshFacilityPreview();
      });

    this.patientAppointmentService.patientAppointments$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.appointments = result;
          this.refreshFacilityPreview();
        }
      });

    // THE HAND-OFF CHANNEL. The caller does `setEditingRiskItem(item)` and THEN navigates, exactly
    // as `task-list.loadTask` does for tasks.
    //
    // The `riskItemId` guard is deliberate. `patient-task-detail` relies on a `setTimeout` inside
    // `resetForm` running AFTER this subscription fires to undo a stale row on the add route; the
    // route param gives the same guarantee without depending on that ordering.
    this.riskItemService.editingRiskItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe(item => {
        if (!this.riskItemId) return;
        if (!item || item.id !== this.riskItemId) return;
        this.editingRiskItem = item;
        this.patchForm();
      });

    // The reload / deep-link fallback described in the class header.
    this.riskItemService.patientRiskItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        if (!this.riskItemId || this.editingRiskItem) return;
        const match = (items || []).find((candidate: any) => candidate && candidate.id === this.riskItemId);
        if (match) {
          this.editingRiskItem = match;
          this.patchForm();
        }
      });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
    // Leave the hand-off channel empty. Nothing else listens to it now that the register opens
    // this page instead of its own dialog, and a row left on it is a row the next `add-risk-item`
    // would have to defend against.
    this.riskItemService.setEditingRiskItem(null);
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  THE FORM
  // ═══════════════════════════════════════════════════════════════════════════════════════

  initForm() {
    this.form = this.fb.group({
      dateOfIssue: [moment(), Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      patientId: [this.patientId, Validators.required],
      // The same fallbacks the add dialog coerces an off-taxonomy suggestion to.
      category: ['process', Validators.required],
      severity: ['medium', Validators.required],
      status: ['Open', Validators.required],
      issue: ['', Validators.required],
      notes: [''],
      // The facility picker. Form controls rather than `[(ngModel)]` because they sit inside the
      // same `[formGroup]` as everything else, and Angular refuses to mix the two in one form.
      //
      // ONE control for the mode AND the hospital: `roomsValue` for an in-rooms / OPD issue, a
      // hospital id otherwise. `null` is reachable but not selectable — it is what a stored
      // hospital that is not among the active ones leaves behind, and it renders as a blank select
      // beside the "recorded as …" line rather than as a choice the doctor made.
      facilitySelection: [this.roomsValue],
      facilityWardId: [null],
    });
  }

  patchForm() {
    if (!this.editingRiskItem) {
      return;
    }
    const item = this.editingRiskItem;

    this.form.patchValue({
      // `dateOfIssue` crosses JSON as `'YYYY-MM-DD'` with no timezone (DATEONLY). Parsed off that
      // string with an explicit format so a browser west of UTC cannot shift the day by one — the
      // failure that is invisible in this timezone and wrong in another.
      dateOfIssue: item.dateOfIssue ? moment(String(item.dateOfIssue).slice(0, 10), 'YYYY-MM-DD') : moment(),
      category: item.category || 'process',
      severity: item.severity || 'medium',
      status: item.status || 'Open',
      issue: item.issue || '',
      notes: item.notes || '',
      groupId: item.groupId || patientGroupId(this.patient, this.groupId),
      patientId: item.patientId || this.patientId,
    });

    this.applyStoredFacility();
    this.loadMedia(item);
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  §28 / P5-P6 — PHOTOGRAPHS
  // ═══════════════════════════════════════════════════════════════════════════════════════
  //
  // The three handlers below are `patient-note-detail`'s, deliberately unchanged in shape. Two
  // arrays, exactly as that component keeps two:
  //
  //   `loadedMediaObjects` — bound to `[fileObjects]`, the gallery's starting contents.
  //   `mediaObjects`       — the WORKING set, what actually gets saved.
  //
  // They are separate because `app-file-upload` re-emits its WHOLE accumulated array on every
  // `fileUpload` in multiple mode. Feeding that straight back into the input would append the same
  // objects again on the next emit.

  /** The gallery's contents on load: `[fileObjects]` is bound to this. */
  loadedMediaObjects: FileObject[] = [];

  /** The working set — saved photographs plus anything added this session. */
  mediaObjects: FileObject[] = [];

  /**
   * Seeds both arrays from the row's `media` association.
   *
   * `mapMediaToFileObject` turns server rows into `FileObject`s WITH their `id`, which is what
   * marks them as already uploaded — `PatientRiskItemService` only sends bytes for objects
   * lacking one, so a save that touches nothing re-uploads nothing.
   */
  private loadMedia(item: any) {
    const media = Array.isArray(item?.media) ? item.media : [];
    this.loadedMediaObjects = media.length ? mapMediaToFileObject(media) : [];
    this.mediaObjects = [...this.loadedMediaObjects];
  }

  /**
   * De-duplicated BY `url`, which is not defensive noise: in `allowMultiple` mode the component
   * emits its entire array each time, so a second photograph arrives as `[first, second]` and a
   * plain append would store the first one twice.
   */
  onRiskMediaUpload(files: FileObject[]) {
    const incoming = (files || []).filter(
      file => !this.mediaObjects.some(existing => existing.url === file.url)
    );
    this.mediaObjects = [...this.mediaObjects, ...incoming];
  }

  /** The gallery's editor owns ordering and annotation, so this is a straight replacement. */
  onRiskMediaEdited(files: FileObject[]) {
    this.mediaObjects = files || [];
  }

  /**
   * Dropped locally only. A photograph that is already a server row is NOT deleted here — it is
   * simply left out of `existingMedia` on the next save, and the server soft-deletes it then.
   *
   * That is why there is no `deleteRiskItemMedia` endpoint and none was added: a delete that took
   * effect immediately would remove evidence from a register even if the doctor then abandoned the
   * edit, and on this screen "abandon the edit" is one back-button press away.
   */
  onRiskMediaDelete(fileObject: FileObject) {
    this.mediaObjects = this.mediaObjects.filter(file => file.url !== fileObject.url);
  }

  /**
   * How many stored photographs were attached AFTER this item was reported.
   *
   * ── ATTACHING TO A `Reported` ITEM IS ALLOWED. DO NOT ADD A GATE. ─────────────────────
   *
   * USER DECISION, 2026-09-01: *"yes leave it up to the user, same as the facility edit."*
   *
   * The PDF has already gone to the hospital, so a photograph attached now is NOT in the document
   * they hold — the stored evidence and the sent document diverge. That is accepted deliberately,
   * and it is the same call §18 already made for the facility snapshot, where a `reportedAt` gate
   * was proposed and explicitly declined. An issue gets reported and then more evidence surfaces;
   * a register that REFUSES the evidence is worse than one whose export is a point-in-time
   * snapshot. There is no block, no confirmation and no disabled state on this path, on purpose —
   * note that the `status` control on this very form is freely editable for the same reason.
   *
   * The count is stated quietly under the upload control instead, where the doctor is already
   * looking at the photographs and the status together.
   */
  get photosAddedAfterReporting(): number {
    const reportedAt = this.editingRiskItem?.reportedAt;
    if (!reportedAt) return 0;
    const reportedMs = new Date(reportedAt).getTime();
    const media = Array.isArray(this.editingRiskItem?.media) ? this.editingRiskItem.media : [];
    return media.filter((row: any) => row?.createdAt && new Date(row.createdAt).getTime() > reportedMs).length;
  }

  resetForm() {
    setTimeout(() => {
      this.editingRiskItem = null;
      this.facilityTouched = false;
      // §28 — both arrays, or the next item opened on this screen inherits the last one's
      // photographs and would attach them to the wrong issue on save.
      this.loadedMediaObjects = [];
      this.mediaObjects = [];
      this.form.reset();
      this.initForm();
      this.refreshFacilityPreview();
      this.cd.detectChanges();
    });
  }

  /** The toolbar's delete gate, and the add/edit discriminator for the facility picker. */
  get isEditing(): boolean {
    return !!(this.editingRiskItem && this.editingRiskItem.id);
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  D8 — THE VERBATIM QUOTE, READ-ONLY
  // ═══════════════════════════════════════════════════════════════════════════════════════

  /**
   * §1 and D8: "No editing of an item's AI-extracted quote. The quote is evidence; it is verbatim
   * or it is gone." It is a getter over the loaded row and has no form control, so there is no
   * input for it to be typed into; the server enforces the same rule by omitting `quote` from
   * `UPDATABLE_FIELDS`.
   */
  get quote(): string | null {
    const value = this.editingRiskItem?.quote;
    return value ? String(value) : null;
  }

  /**
   * Hand-entered — no `aiRequestId`, so no extractor and never a quote. TRUE ON THE ADD ROUTE by
   * construction, since there is no row yet.
   *
   * This exists for the missing-quote wording and nothing else, and the distinction is the add
   * dialog's `isManualEntry` precedent verbatim: an AI suggestion that arrived WITHOUT its quote
   * is a genuine loss of the D8 evidence control and is worth flagging; a typed item never had one
   * and never should, so warning about it trains the user to ignore the warning that means
   * something.
   */
  get isManualEntry(): boolean {
    return !this.editingRiskItem?.aiRequestId;
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  THE FACILITY PICKER (§12 — THE CLIENT DECIDES)
  // ═══════════════════════════════════════════════════════════════════════════════════════

  /** Read through the control, not `form.value`: a DISABLED control is absent from `form.value`. */
  get facilitySelection(): string | null {
    return this.form?.get('facilitySelection')?.value || null;
  }

  /**
   * DERIVED, not stored. The concept is still load-bearing — D6's `location` column,
   * `buildFacilityChoice` and both facility-line builders all speak in these two terms — but it is
   * no longer a control the doctor can move independently of the hospital, which is what made the
   * unsaveable state possible.
   *
   * A `null` selection derives 'facility', which is deliberate and matches the old default: the
   * only way to reach `null` is a stored or previewed hospital that is not among the active ones,
   * and that row DOES name a facility. It cannot be reached by choosing.
   */
  get facilityMode(): string {
    return this.facilitySelection === this.roomsValue ? 'rooms' : 'facility';
  }

  get selectedHospitalId(): string | null {
    const selection = this.facilitySelection;
    return selection && selection !== this.roomsValue ? selection : null;
  }

  get selectedWardId(): string | null {
    return this.form?.get('facilityWardId')?.value || null;
  }

  /**
   * ONE handler for what used to be `onFacilityModeChange` + `onHospitalChange`, because there is
   * now one control. Both of its jobs survive intact:
   *
   *  - In-rooms / OPD ⇒ `selectedHospitalId` is null ⇒ `syncWards` yields `[]` ⇒ the ward select
   *    disappears and the ward is nulled. 'rooms' means all three snapshot columns NULL (D6).
   *  - A different hospital ⇒ the wards repopulate and the previously chosen ward is CLEARED, so a
   *    ward from the old hospital cannot survive the change and be snapshotted against a hospital
   *    it does not belong to (`admit-details.component.ts`'s `selectHospital`, plus that clear).
   */
  onFacilityChange(): void {
    this.facilityTouched = true;
    this.syncWards();
    this.form.patchValue({ facilityWardId: null });
  }

  onWardChange(): void {
    this.facilityTouched = true;
  }

  /**
   * §4 branches 2 and 3 both key off the DAY, and on this page the doctor sets that day here
   * rather than the caller setting it before a dialog opens. So the default is re-previewed when
   * the date moves — but only while the picker is untouched, because a choice already made outranks
   * a default.
   */
  onDateOfIssueChange(): void {
    this.refreshFacilityPreview();
  }

  /**
   * `needsHospitalSelection` USED TO SIT HERE, and it is gone rather than left as a guard that can
   * never fire. It described "the picker was moved into facility mode but no hospital was chosen",
   * which took two controls to express. With one select the only two things a doctor can DO are
   * choose `roomsValue` or choose a hospital id — a mat-select offers no way to select nothing —
   * so `facilityTouched === true` now implies a non-null selection, and the state is unreachable
   * by construction rather than by validation.
   *
   * The blank select still happens; it is just never something the doctor did. It is the untouched
   * shape of a row naming a hospital that is not among the practice's active ones, and blocking
   * THAT was always wrong — the untouched path posts no `facilityChoice` at all and the stored
   * value survives. That is why the guard carried a `facilityTouched` term in the first place.
   */
  get canSave(): boolean {
    return !this.saving && !this.form.invalid;
  }

  /** What the picker currently says, for the "you changed this" line. */
  get chosenFacilityLine(): string {
    if (this.facilityMode === 'rooms') return 'Your rooms — no hospital or ward';
    const hospital = this.hospitalById(this.selectedHospitalId);
    const ward = this.wards.find(candidate => candidate.id === this.selectedWardId);
    const parts = [hospital?.name, ward?.name].filter(part => !!part);
    return parts.join(' — ') || 'No hospital selected';
  }

  /** The pre-filled default, in words. Empty for an in-rooms issue, which is correct. */
  get previewFacilityLine(): string {
    const parts = [this.facilityPreview?.hospitalName, this.facilityPreview?.wardName].filter(part => !!part);
    return parts.join(' — ');
  }

  /**
   * What the picker said BEFORE the doctor touched it, for the "you have changed this from …"
   * line — the §4 preview on the add route, the stored snapshot on the edit route. One getter
   * rather than two branches in the template, because the two routes differ only in where the
   * default came from and the sentence is the same either way.
   */
  get originalFacilityLine(): string {
    return this.isEditing ? this.storedFacilityLine : this.previewFacilityLine;
  }

  /** The SNAPSHOT already stored on the row being edited (D5 — kept as text, not a live join). */
  get storedFacilityLine(): string {
    const item = this.editingRiskItem;
    if (!item) return '';
    const parts = [item.hospitalName, item.wardName].filter((part: any) => !!part);
    return parts.join(' — ');
  }

  private refreshFacilityPreview(): void {
    // The snapshot is decided ONCE, at creation. Nothing to preview on the edit route.
    if (this.isEditing || this.riskItemId) return;
    if (this.facilityTouched) return;
    if (!this.patient || !this.form) return;

    const dateOfIssue = this.form.getRawValue().dateOfIssue || moment();
    // The server runs §4 branch 2 off the `appointmentId` IN THE BODY and nothing else, so the
    // preview is given the same one `saveRiskItem` will post.
    const encounter = pickEncounterAppointment(this.appointments, dateOfIssue, this.patient);

    this.facilityPreview = previewRiskLocation({
      patient: this.patient,
      hospitals: this.hospitals,
      appointments: this.appointments,
      appointmentId: encounter?.id ?? null,
      admissionRecords: this.admissionRecords,
      dateOfIssue,
      groupId: this.patient.groupId || this.groupId || null,
    });

    // Branch 4 ('rooms') pre-fills In-rooms / OPD, which under D2 is a first-class outcome and not
    // a failure — a legitimate pre-filled answer, not an empty form to complete. It is now the
    // FIRST OPTION of the same select the hospitals are in, so that outcome is named as a place
    // sitting alongside the others rather than as the off position of a toggle.
    //
    // Only an id we can actually show as selected. A branch-3 name match that found no ACTIVE
    // hospital leaves `hospitalId` null beside a good `hospitalName`: the select then starts EMPTY
    // while the line above still shows the recorded name. Deliberate — the alternative is
    // inventing a selection the preview did not make. `facilityMode` derives 'facility' from that
    // null, which is what keeps the "recorded as …" wording and the empty-list hint on screen.
    const previewIsRooms = this.facilityPreview.location === 'rooms';
    const previewHospitalId = this.facilityPreview.hospitalId || null;
    this.form.patchValue({
      facilitySelection: previewIsRooms
        ? this.roomsValue
        : (previewHospitalId && this.hospitals.some(hospital => hospital.id === previewHospitalId)
          ? previewHospitalId
          : null)
    }, { emitEvent: false });

    this.syncWards();
    this.form.patchValue({ facilityWardId: this.matchWardIdByName(this.facilityPreview.wardName) }, { emitEvent: false });
  }

  /**
   * The edit route's half: seed the same controls from the STORED snapshot, and leave them
   * EDITABLE. `updateRiskItem` now accepts the identical `facilityChoice` envelope, so a change
   * made here is a change that is actually written — the reason the controls used to be locked is
   * gone.
   *
   * The snapshot is still never RE-RESOLVED (`refreshFacilityPreview` returns early on this
   * route): §4 runs off admission data that has moved on since, and re-running it on an edit is
   * the live-join behaviour §4 exists to prevent. The stored value is the default; only the doctor
   * changes it.
   *
   * THE `facilityTouched` GUARD IS LOAD-BEARING NOW THAT THE CONTROLS ARE LIVE. This runs again on
   * every `hospitals$` emission (the list can arrive after the row), and without the guard a late
   * emission would silently reset the doctor's selection back to the stored one — the mirror of
   * what `refreshFacilityPreview` already guards against on the add route.
   */
  private applyStoredFacility(): void {
    const item = this.editingRiskItem;
    if (!item || !this.form) return;
    if (this.facilityTouched) return;

    // The stored `location` column decides which of the two shapes the one select opens in, exactly
    // as it decided the toggle before: 'rooms' (or a null column) is In-rooms / OPD, anything else
    // names a hospital. A stored hospital that is no longer among the practice's ACTIVE ones leaves
    // the select blank — see the "recorded as …" line in the template, which is the only thing
    // standing between that blank and a doctor "fixing" a facility that was correct.
    const storedIsRooms = !item.location || item.location === 'rooms';
    const storedHospitalId = !storedIsRooms
      && item.hospitalId
      && this.hospitals.some(hospital => hospital.id === item.hospitalId)
      ? item.hospitalId
      : null;
    this.form.patchValue({
      facilitySelection: storedIsRooms ? this.roomsValue : storedHospitalId
    }, { emitEvent: false });
    this.syncWards();
    this.form.patchValue({ facilityWardId: this.matchWardIdByName(item.wardName) }, { emitEvent: false });
  }

  private syncWards(): void {
    const hospital = this.hospitalById(this.selectedHospitalId);
    const wards = hospital && Array.isArray(hospital.wards) ? hospital.wards : [];
    this.wards = wards
      .filter(ward => !!ward && !!ward.id)
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }

  /**
   * The preview and the stored row both yield a ward NAME (branch 3 can only ever have one —
   * `PatientAdmissionHistory.ward` is a plain string). The select needs an id, so the name is
   * matched back within the chosen hospital using the resolver's own normalisation: trim and
   * lowercase, nothing cleverer. No match leaves the ward unselected rather than guessing.
   */
  private matchWardIdByName(name?: string | null): string | null {
    const target = String(name || '').trim().toLowerCase();
    if (!target) return null;
    const match = this.wards.find(ward => String(ward.name || '').trim().toLowerCase() === target);
    return match?.id || null;
  }

  private hospitalById(id: string | null): RiskPreviewHospital | null {
    return this.hospitals.find(hospital => hospital.id === id) || null;
  }

  /**
   * The four stored columns, or `null` when the picker was never moved — see `facilityTouched`.
   *
   * `location` is decided HERE and not server-side (§12): a hospital the §4 preview had already
   * identified as a booked PROCEDURE stays 'procedure' (the doctor corrected WHERE, not what kind
   * of encounter it was); anything else naming a hospital is 'inpatient'.
   *
   * ON THE EDIT ROUTE THE STORED ROW PLAYS THE PREVIEW'S PART. `facilityPreview` is null there by
   * design, so reading only it would quietly demote every edited 'procedure' item to 'inpatient'
   * the first time someone corrected its ward — a silent change to what kind of encounter the
   * register says this was, made by a doctor who only moved a hospital.
   */
  private buildFacilityChoice(): RiskFacilityChoice | null {
    if (!this.facilityTouched) return null;

    if (this.facilityMode === 'rooms') {
      return { hospitalId: null, hospitalName: null, wardName: null, location: 'rooms' };
    }

    const hospital = this.hospitalById(this.selectedHospitalId);
    const ward = this.wards.find(candidate => candidate.id === this.selectedWardId) || null;
    const wasProcedure = this.isEditing
      ? this.editingRiskItem?.location === 'procedure'
      : this.facilityPreview?.location === 'procedure';
    return {
      hospitalId: this.selectedHospitalId,
      hospitalName: hospital?.name ? String(hospital.name).trim() : null,
      wardName: ward?.name ? String(ward.name).trim() : null,
      location: wasProcedure ? 'procedure' : 'inpatient',
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  DISPLAY LABELS — all by import (D7/D9)
  // ═══════════════════════════════════════════════════════════════════════════════════════

  categoryLabel(category: string): string {
    return riskCategoryLabel(category);
  }

  severityLabel(severity: string): string {
    return riskSeverityLabel(severity);
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  SAVE / DELETE
  // ═══════════════════════════════════════════════════════════════════════════════════════

  saveRiskItem() {
    if (!this.canSave) return;

    const raw = this.form.getRawValue();
    const dateOfIssue: moment.Moment = moment.isMoment(raw.dateOfIssue) ? raw.dateOfIssue : moment(raw.dateOfIssue);
    // `dateOfIssue` is DATEONLY (§5c) — the calendar day AT THE FACILITY. Formatted, never
    // `.utc().toISOString()`: see the class header.
    const day = dateOfIssue.format('YYYY-MM-DD');
    const issue = String(raw.issue || '').trim();
    const notes = String(raw.notes || '').trim() || null;

    if (this.isEditing) {
      // The server's `UPDATABLE_FIELDS`, plus the facility as its own envelope. `quote` is still
      // absent because the server still refuses it (D8), and sending it would read as if it
      // applied.
      const updates = {
        category: raw.category,
        severity: raw.severity,
        status: raw.status,
        dateOfIssue: day,
        issue,
        notes,
        // §12 on the edit route. NULL when the picker was never moved, and NULL is what makes the
        // server LEAVE THE STORED FACILITY ALONE — an untouched picker must not be able to null a
        // hospital by round-tripping an empty select, which is the exact shape of a row whose
        // `hospitalName` matched no active hospital (branch 3, or a deactivated hospital).
        facilityChoice: this.buildFacilityChoice(),
        // §28 / P5. ALWAYS sent from this screen, even when empty — the doctor can see the whole
        // gallery while they edit, so `[]` here genuinely means "no photographs" and the server
        // prunes accordingly. That is the exact OPPOSITE of the narrow callers (`updateStatus` in
        // the register, `saveNotes` in the detail dialog), which omit the key entirely and must
        // therefore prune nothing.
        media: this.mediaObjects,
      };

      this.saving = true;
      this.riskItemService.updateRiskItem(this.editingRiskItem.id, updates)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.saving = false;
            this.showToastMessage('Risk item updated successfully');
            this.returnToParent();
          },
          error: (error) => {
            this.saving = false;
            console.error('Error updating risk item: ', error);
            this.showToastMessage('There was an error updating the risk item. Please try again.');
          }
        });
      return;
    }

    // ── `patientId` IS NEVER NULL ON A NEW ITEM (user decision, 2026-09-01) ─────────────────
    //
    // Enforced in the workflow and the UI, NOT at the database layer — the column stays nullable
    // and there is no migration. This page is only ever reached from inside a patient file, so the
    // patient is genuinely in context and is taken from `currentPatient$`; the doctor is never
    // asked to pick one.
    //
    // The control already carries `Validators.required` and `canSave` gates the save button on it,
    // so this is the second guard rather than the first. It is here because the first one is a
    // form validator that a later hand can loosen without noticing what it was holding up, and
    // because "the patient is still loading" is the honest message for the one case that reaches
    // it — the same guard, in the same words, that both dialog creation paths already use.
    if (!raw.patientId) {
      this.showToastMessage('The patient is still loading. Please try again in a moment.');
      return;
    }

    const encounter = pickEncounterAppointment(this.appointments, dateOfIssue, this.patient);

    const riskItemData = {
      groupId: raw.groupId,
      patientId: raw.patientId,
      // D13 — hand-added, so there is no provenance. NULL is the legal, meaningful value and is
      // what marks the item as entered by a person rather than extracted.
      aiRequestId: null,
      category: raw.category,
      severity: raw.severity,
      status: raw.status,
      dateOfIssue: day,
      issue,
      // ALWAYS NULL on this path. D8 is about not FABRICATING a verbatim quote; a typed item has
      // nothing verbatim behind it, and inventing one is the exact failure mode D8 guards.
      quote: null,
      notes,
      appointmentId: encounter?.id ?? null,
      // §12 — the client decides. NULL when the doctor left the pre-filled default alone, in which
      // case the server's §4 chain supplies it. The server does not validate the choice and has no
      // rejection path, so there is nothing to reconcile afterwards.
      facilityChoice: this.buildFacilityChoice(),
      // §28 / P5 — the photographs, attached on the same request that creates the item. The
      // service turns this into multipart and strips the key from the JSON; with an empty array
      // the request stays plain JSON exactly as it was before this workstream.
      media: this.mediaObjects,
    };

    this.saving = true;
    // No local insert — `PatientRiskItemService`'s `riskItemAdded` socket handler appends the row
    // the server actually wrote, facility columns and all.
    this.riskItemService.addRiskItem(riskItemData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.showToastMessage('Risk item saved successfully');
          this.returnToParent();
        },
        error: (error) => {
          this.saving = false;
          console.error('Error saving risk item: ', error);
          this.showToastMessage('There was an error saving the risk item. Please try again.');
        }
      });
  }

  /** N6 — soft delete (`active: false`) server-side, behind the same confirm as `deleteTask`. */
  async deleteRiskItem() {
    if (!this.isEditing) return;

    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    this.riskItemService.deleteRiskItem(this.editingRiskItem.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage('Risk item deleted successfully');
          this.returnToParent();
        },
        error: (error) => {
          console.error('Error deleting risk item: ', error);
          this.showToastMessage('There was an error deleting the risk item. Please try again.');
        }
      });
  }

  returnToParent() {
    let isDischarge = false;
    const currentUrl = this.router.url;
    if (currentUrl.includes('discharge')) {
      isDischarge = true;
    }
    if (!isDischarge) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patientId}`] } }]);
    } else {
      this.router.navigate(['/patients', { outlets: { patient: ['discharge', `${this.patientId}`] } }]);
    }
  }

}
